import type { ReadinessReport } from "@/features/generation-readiness/check-readiness";
import {
  parseApplicationOntologyGenerationBootstrapInput,
  type ApplicationOntologyGenerationBootstrapInput,
} from "@/features/application-ontology/application-ontology-generation";

export const PROJECT_CHAT_THREAD_SCHEMA_VERSION =
  "cogo.studio-project-chat-thread.v1" as const;

const PROJECT_CHAT_THREAD_STORAGE_PREFIX = "cogo-studio:project-chat-thread:v1";
const MAX_PROJECT_CHAT_TURNS = 200;
const MAX_PROJECT_CHAT_TURN_BYTES = 32 * 1024;
const MAX_PROJECT_CHAT_THREAD_BYTES = 256 * 1024;

export const PROJECT_CHAT_PRIMARY_ROUTES = [
  "read-only",
  "grounded-edit",
  "generation",
  "agent-runtime",
  "studio-operation",
] as const;

export type ProjectChatPrimaryRoute =
  (typeof PROJECT_CHAT_PRIMARY_ROUTES)[number];

export interface ProjectChatThreadScope {
  userId: string;
  projectId: string;
}

export interface ProjectChatRetryMetadata {
  retryable: boolean;
  route: ProjectChatPrimaryRoute;
  intent: string;
}

export interface ProjectChatGenerationBinding {
  projectId: string;
  requestId?: string;
  jobId?: string;
}

export type ProjectChatGenerationAuthority =
  | { kind: "unbound" }
  | { kind: "unavailable" }
  | {
      kind: "application-ontology";
      bootstrap: ApplicationOntologyGenerationBootstrapInput;
    };

export const PROJECT_CHAT_OUTCOME_STATUSES = [
  "pending",
  "succeeded",
  "reverted",
  "refused",
  "transport-failure",
  "failed",
] as const;

export type ProjectChatOutcomeStatus =
  (typeof PROJECT_CHAT_OUTCOME_STATUSES)[number];

export type ProjectChatThreadTurn =
  | {
      kind: "user";
      id: string;
      timestamp: number;
      route: ProjectChatPrimaryRoute;
      text: string;
      presentation?: "assistant-thread";
    }
  | {
      kind: "outcome";
      id: string;
      timestamp: number;
      route: ProjectChatPrimaryRoute;
      status: ProjectChatOutcomeStatus;
      text: string;
      retry: ProjectChatRetryMetadata;
      generation?: ProjectChatGenerationBinding;
      presentation?: "assistant-thread";
    }
  | {
      kind: "generation-readiness";
      id: string;
      timestamp: number;
      prompt: string;
      report: ReadinessReport;
      generationAuthority?: ProjectChatGenerationAuthority;
    }
  | {
      kind: "template-proposal";
      id: string;
      timestamp: number;
      templateId: string;
      displayName: string;
      prompt: string;
      matchedTerms: string[];
    };

export interface ProjectChatThread {
  schemaVersion: typeof PROJECT_CHAT_THREAD_SCHEMA_VERSION;
  userId: string;
  projectId: string;
  updatedAt: number;
  turns: ProjectChatThreadTurn[];
}

export function projectChatOutcomeIdForSourceMessage(
  sourceMessageId: string,
): string {
  return `chat-outcome-${sourceMessageId}`;
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function finiteTimestamp(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}

function primaryRoute(value: unknown): value is ProjectChatPrimaryRoute {
  return PROJECT_CHAT_PRIMARY_ROUTES.includes(value as ProjectChatPrimaryRoute);
}

function retryMetadata(value: unknown): value is ProjectChatRetryMetadata {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const retry = value as Record<string, unknown>;
  return (
    typeof retry.retryable === "boolean" &&
    primaryRoute(retry.route) &&
    nonEmptyString(retry.intent)
  );
}

function generationBinding(value: unknown): value is ProjectChatGenerationBinding {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const binding = value as Record<string, unknown>;
  return (
    nonEmptyString(binding.projectId) &&
    (binding.requestId === undefined || nonEmptyString(binding.requestId)) &&
    (binding.jobId === undefined || nonEmptyString(binding.jobId))
  );
}

function generationAuthority(
  value: unknown,
): value is ProjectChatGenerationAuthority {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const authority = value as Record<string, unknown>;
  if (authority.kind === "unbound" || authority.kind === "unavailable") {
    return Object.keys(authority).length === 1;
  }
  return (
    authority.kind === "application-ontology" &&
    Object.keys(authority).length === 2 &&
    parseApplicationOntologyGenerationBootstrapInput(authority.bootstrap) !== null
  );
}

function stringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => nonEmptyString(item));
}

function readinessReport(value: unknown): value is ReadinessReport {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const report = value as Record<string, unknown>;
  if (
    (report.status !== "success" && report.status !== "error") ||
    !Number.isInteger(report.blockingCount) ||
    (report.blockingCount as number) < 0 ||
    !nonEmptyString(report.checkedAt) ||
    !Array.isArray(report.items) ||
    !report.machineValidation ||
    typeof report.machineValidation !== "object" ||
    Array.isArray(report.machineValidation)
  ) {
    return false;
  }
  const machine = report.machineValidation as Record<string, unknown>;
  if (
    typeof machine.performed !== "boolean" ||
    (machine.reason !== undefined && typeof machine.reason !== "string") ||
    report.designSystem !== null
  ) {
    return false;
  }
  return report.items.every((value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return false;
    const item = value as Record<string, unknown>;
    if (
      !["blocking", "unfilled", "advisory"].includes(item.kind as string) ||
      !nonEmptyString(item.title) ||
      !nonEmptyString(item.explanation) ||
      !Array.isArray(item.evidenceRefs) ||
      !item.evidenceRefs.every((ref) => typeof ref === "string") ||
      (item.machineCode !== undefined && typeof item.machineCode !== "string")
    ) {
      return false;
    }
    if (item.suggestion === undefined) return true;
    if (
      !item.suggestion ||
      typeof item.suggestion !== "object" ||
      Array.isArray(item.suggestion)
    ) {
      return false;
    }
    const suggestion = item.suggestion as Record<string, unknown>;
    return (
      nonEmptyString(suggestion.label) &&
      (suggestion.navigateTo === undefined ||
        typeof suggestion.navigateTo === "string")
    );
  });
}

function projectChatTurn(value: unknown): value is ProjectChatThreadTurn {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const turn = value as Record<string, unknown>;
  if (!nonEmptyString(turn.id) || !finiteTimestamp(turn.timestamp)) return false;
  if (turn.kind === "generation-readiness") {
    return (
      nonEmptyString(turn.prompt) &&
      readinessReport(turn.report) &&
      (turn.generationAuthority === undefined ||
        generationAuthority(turn.generationAuthority))
    );
  }
  if (turn.kind === "template-proposal") {
    return (
      nonEmptyString(turn.templateId) &&
      nonEmptyString(turn.displayName) &&
      nonEmptyString(turn.prompt) &&
      stringArray(turn.matchedTerms)
    );
  }
  if (!primaryRoute(turn.route) || !nonEmptyString(turn.text)) return false;
  if (
    turn.presentation !== undefined &&
    turn.presentation !== "assistant-thread"
  ) {
    return false;
  }
  if (turn.kind === "user") return true;
  return (
    turn.kind === "outcome" &&
    PROJECT_CHAT_OUTCOME_STATUSES.includes(
      turn.status as ProjectChatOutcomeStatus,
    ) &&
    retryMetadata(turn.retry) &&
    (turn.generation === undefined ||
      (turn.route === "generation" && generationBinding(turn.generation)))
  );
}

function utf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

function boundedProjectChatTurn(value: unknown): value is ProjectChatThreadTurn {
  return (
    projectChatTurn(value) &&
    utf8ByteLength(JSON.stringify(value)) <= MAX_PROJECT_CHAT_TURN_BYTES
  );
}

function parseProjectChatThread(
  value: unknown,
  scope: ProjectChatThreadScope,
): ProjectChatThread | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const thread = value as Record<string, unknown>;
  if (
    thread.schemaVersion !== PROJECT_CHAT_THREAD_SCHEMA_VERSION ||
    thread.userId !== scope.userId ||
    thread.projectId !== scope.projectId ||
    !finiteTimestamp(thread.updatedAt) ||
    !Array.isArray(thread.turns) ||
    thread.turns.length > MAX_PROJECT_CHAT_TURNS ||
    !thread.turns.every(boundedProjectChatTurn)
  ) {
    return null;
  }
  return {
    schemaVersion: PROJECT_CHAT_THREAD_SCHEMA_VERSION,
    userId: scope.userId,
    projectId: scope.projectId,
    updatedAt: thread.updatedAt,
    turns: thread.turns as ProjectChatThreadTurn[],
  };
}

function browserStorage(storage?: Storage): Storage | null {
  if (storage) return storage;
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function projectChatThreadStorageKey(
  scope: ProjectChatThreadScope,
): string {
  return `${PROJECT_CHAT_THREAD_STORAGE_PREFIX}:${encodeURIComponent(scope.userId)}:${encodeURIComponent(scope.projectId)}`;
}

/**
 * Narrow consumer API for the creator Chat surface. Invalid, stale-schema, or
 * scope-mismatched bytes are removed and never projected into another user's
 * project thread.
 */
export function readProjectChatThread(
  scope: ProjectChatThreadScope,
  storage?: Storage,
): ProjectChatThread | null {
  const target = browserStorage(storage);
  if (!target) return null;
  const key = projectChatThreadStorageKey(scope);
  let raw: string | null;
  try {
    raw = target.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed =
      utf8ByteLength(raw) <= MAX_PROJECT_CHAT_THREAD_BYTES
        ? parseProjectChatThread(JSON.parse(raw), scope)
        : null;
    if (parsed) return parsed;
  } catch {
    // The same fail-closed path below handles malformed JSON.
  }
  try {
    target.removeItem(key);
  } catch {
    // A denied cleanup is still a persistence miss for this caller.
  }
  return null;
}

function writeProjectChatThreadKeepingTurn(
  scope: ProjectChatThreadScope,
  target: Storage,
  inputTurns: readonly ProjectChatThreadTurn[],
  updatedAt: number,
  protectedTurnId: string,
): ProjectChatThread | null {
  const turns = [...inputTurns].slice(-MAX_PROJECT_CHAT_TURNS);
  while (turns.some((turn) => turn.id === protectedTurnId)) {
    const thread: ProjectChatThread = {
      schemaVersion: PROJECT_CHAT_THREAD_SCHEMA_VERSION,
      userId: scope.userId,
      projectId: scope.projectId,
      updatedAt,
      turns,
    };
    const serialized = JSON.stringify(thread);
    if (utf8ByteLength(serialized) <= MAX_PROJECT_CHAT_THREAD_BYTES) {
      try {
        target.setItem(projectChatThreadStorageKey(scope), serialized);
        return thread;
      } catch {
        // Match append's quota policy below: retain the settled action and
        // evict the oldest unrelated history until the newest truth fits.
      }
    }
    const evictionIndex = turns.findIndex(
      (turn) => turn.id !== protectedTurnId,
    );
    if (evictionIndex < 0) return null;
    turns.splice(evictionIndex, 1);
  }
  return null;
}

export function appendProjectChatThreadTurn(
  scope: ProjectChatThreadScope,
  turn: ProjectChatThreadTurn,
  storage?: Storage,
): ProjectChatThread | null {
  const target = browserStorage(storage);
  if (!target || !boundedProjectChatTurn(turn)) return null;
  const existing = readProjectChatThread(scope, target);
  const turns = [...(existing?.turns ?? []), turn].slice(
    -MAX_PROJECT_CHAT_TURNS,
  );
  while (turns.length > 0) {
    const thread: ProjectChatThread = {
      schemaVersion: PROJECT_CHAT_THREAD_SCHEMA_VERSION,
      userId: scope.userId,
      projectId: scope.projectId,
      updatedAt: turn.timestamp,
      turns,
    };
    const serialized = JSON.stringify(thread);
    if (utf8ByteLength(serialized) > MAX_PROJECT_CHAT_THREAD_BYTES) {
      turns.shift();
      continue;
    }
    try {
      target.setItem(projectChatThreadStorageKey(scope), serialized);
      return thread;
    } catch {
      // Browsers can expose less than their nominal quota when other Studio
      // data already occupies the origin. Retrying with the oldest turn
      // removed keeps the newest exchange appendable without clearing any
      // unrelated storage keys.
      turns.shift();
    }
  }
  return null;
}

export function settleProjectChatThreadOutcome(
  scope: ProjectChatThreadScope,
  outcomeId: string,
  settlement: {
    status: Exclude<ProjectChatOutcomeStatus, "pending">;
    text: string;
    retryable: boolean;
    timestamp: number;
  },
  storage?: Storage,
): ProjectChatThread | null {
  const target = browserStorage(storage);
  if (!target || !nonEmptyString(settlement.text)) return null;
  const existing = readProjectChatThread(scope, target);
  if (!existing) return null;
  const index = existing.turns.findIndex(
    (turn) => turn.kind === "outcome" && turn.id === outcomeId,
  );
  const current = existing.turns[index];
  if (!current || current.kind !== "outcome") return null;
  const canSettle =
    current.status === "pending" ||
    (current.status === "succeeded" && settlement.status === "reverted") ||
    (current.status === settlement.status &&
      current.retry.retryable &&
      !settlement.retryable);
  if (!canSettle) return existing;

  const settledTurn: ProjectChatThreadTurn = {
    ...current,
    timestamp: settlement.timestamp,
    status: settlement.status,
    text: settlement.text,
    retry: {
      ...current.retry,
      retryable: settlement.retryable,
    },
  };
  if (!boundedProjectChatTurn(settledTurn)) return null;
  const turns = [...existing.turns];
  turns[index] = settledTurn;
  return writeProjectChatThreadKeepingTurn(
    scope,
    target,
    turns,
    settlement.timestamp,
    outcomeId,
  );
}

/**
 * Atomically replace an actionable persisted descriptor with its terminal
 * creator-visible outcome. Writing one replacement avoids a crash window in
 * which a two-step append/consume could either lose the answer or leave the
 * old action live beside it.
 */
export function settleProjectChatThreadDescriptor(
  scope: ProjectChatThreadScope,
  sourceTurnId: string,
  settlement: {
    route: ProjectChatPrimaryRoute;
    status: Exclude<ProjectChatOutcomeStatus, "pending">;
    text: string;
    retryable: boolean;
    intent: string;
    timestamp: number;
  },
  storage?: Storage,
): ProjectChatThread | null {
  const target = browserStorage(storage);
  if (
    !target ||
    !nonEmptyString(sourceTurnId) ||
    !nonEmptyString(settlement.text) ||
    !nonEmptyString(settlement.intent)
  ) {
    return null;
  }
  const existing = readProjectChatThread(scope, target);
  if (!existing) return null;
  const outcomeId = projectChatOutcomeIdForSourceMessage(sourceTurnId);
  const existingOutcome = existing.turns.find(
    (turn) => turn.kind === "outcome" && turn.id === outcomeId,
  );
  if (existingOutcome?.kind === "outcome") return existing;
  const index = existing.turns.findIndex((turn) => turn.id === sourceTurnId);
  const source = existing.turns[index];
  if (
    !source ||
    (source.kind !== "generation-readiness" &&
      source.kind !== "template-proposal")
  ) {
    return null;
  }
  const outcome: ProjectChatThreadTurn = {
    kind: "outcome",
    id: outcomeId,
    timestamp: settlement.timestamp,
    route: settlement.route,
    status: settlement.status,
    text: settlement.text,
    retry: {
      retryable: settlement.retryable,
      route: settlement.route,
      intent: settlement.intent,
    },
  };
  if (!boundedProjectChatTurn(outcome)) return null;
  const turns = [...existing.turns];
  turns[index] = outcome;
  return writeProjectChatThreadKeepingTurn(
    scope,
    target,
    turns,
    settlement.timestamp,
    outcome.id,
  );
}

export function replaceProjectChatTemplateWithReadiness(
  scope: ProjectChatThreadScope,
  sourceTurnId: string,
  readiness: Extract<ProjectChatThreadTurn, { kind: "generation-readiness" }>,
  storage?: Storage,
): ProjectChatThread | null {
  const target = browserStorage(storage);
  if (!target || !boundedProjectChatTurn(readiness)) return null;
  const existing = readProjectChatThread(scope, target);
  if (!existing) return null;
  const index = existing.turns.findIndex((turn) => turn.id === sourceTurnId);
  if (existing.turns[index]?.kind !== "template-proposal") return null;
  const turns = [...existing.turns];
  turns[index] = readiness;
  return writeProjectChatThreadKeepingTurn(
    scope,
    target,
    turns,
    readiness.timestamp,
    readiness.id,
  );
}

export function beginProjectChatGeneration(
  scope: ProjectChatThreadScope,
  sourceTurnId: string,
  text: string,
  timestamp: number,
  storage?: Storage,
): ProjectChatThread | null {
  const target = browserStorage(storage);
  if (!target || !nonEmptyString(text)) return null;
  const existing = readProjectChatThread(scope, target);
  if (!existing) return null;
  const index = existing.turns.findIndex((turn) => turn.id === sourceTurnId);
  const source = existing.turns[index];
  if (!source || source.kind !== "generation-readiness") return null;
  const outcome: ProjectChatThreadTurn = {
    kind: "outcome",
    id: projectChatOutcomeIdForSourceMessage(sourceTurnId),
    timestamp,
    route: "generation",
    status: "pending",
    text,
    retry: {
      retryable: false,
      route: "generation",
      intent: source.prompt,
    },
  };
  if (!boundedProjectChatTurn(outcome)) return null;
  const turns = [...existing.turns];
  turns[index] = outcome;
  return writeProjectChatThreadKeepingTurn(
    scope,
    target,
    turns,
    timestamp,
    outcome.id,
  );
}

export function adoptProjectChatGeneration(
  scope: ProjectChatThreadScope,
  userTurn: Extract<ProjectChatThreadTurn, { kind: "user" }>,
  sourceTurnId: string,
  pendingText: string,
  confirmedIntent: string,
  generation: ProjectChatGenerationBinding,
  timestamp: number,
  storage?: Storage,
): ProjectChatThread | null {
  const target = browserStorage(storage);
  if (
    !target ||
    userTurn.route !== "generation" ||
    !boundedProjectChatTurn(userTurn) ||
    !nonEmptyString(sourceTurnId) ||
    !nonEmptyString(pendingText) ||
    !nonEmptyString(confirmedIntent) ||
    !generationBinding(generation) ||
    !finiteTimestamp(timestamp)
  ) {
    return null;
  }
  const outcome: ProjectChatThreadTurn = {
    kind: "outcome",
    id: projectChatOutcomeIdForSourceMessage(sourceTurnId),
    timestamp,
    route: "generation",
    status: "pending",
    text: pendingText,
    retry: {
      retryable: false,
      route: "generation",
      intent: confirmedIntent,
    },
    generation,
  };
  if (!boundedProjectChatTurn(outcome)) return null;
  const existing = readProjectChatThread(scope, target);
  if (
    existing?.turns.some(
      (turn) => turn.id === userTurn.id || turn.id === outcome.id,
    )
  ) {
    return null;
  }
  return writeProjectChatThreadKeepingTurn(
    scope,
    target,
    [...(existing?.turns ?? []), userTurn, outcome],
    timestamp,
    outcome.id,
  );
}

export function bindProjectChatGeneration(
  scope: ProjectChatThreadScope,
  outcomeId: string,
  generation: ProjectChatGenerationBinding,
  timestamp: number,
  storage?: Storage,
): ProjectChatThread | null {
  const target = browserStorage(storage);
  if (!target || !generationBinding(generation)) return null;
  const existing = readProjectChatThread(scope, target);
  if (!existing) return null;
  const index = existing.turns.findIndex(
    (turn) => turn.kind === "outcome" && turn.id === outcomeId,
  );
  const current = existing.turns[index];
  if (
    !current ||
    current.kind !== "outcome" ||
    current.route !== "generation" ||
    current.status !== "pending"
  ) {
    return null;
  }
  const bound: ProjectChatThreadTurn = {
    ...current,
    timestamp,
    generation,
  };
  if (!boundedProjectChatTurn(bound)) return null;
  const turns = [...existing.turns];
  turns[index] = bound;
  return writeProjectChatThreadKeepingTurn(
    scope,
    target,
    turns,
    timestamp,
    outcomeId,
  );
}

export function settleBoundProjectChatGenerationOutcome(
  userId: string,
  generation: ProjectChatGenerationBinding,
  settlement: {
    status: Exclude<ProjectChatOutcomeStatus, "pending">;
    text: string;
    retryable: boolean;
    timestamp: number;
  },
  storage?: Storage,
): ProjectChatThread | null {
  const target = browserStorage(storage);
  if (!target || !nonEmptyString(userId) || !generationBinding(generation)) {
    return null;
  }
  const keyPrefix = `${PROJECT_CHAT_THREAD_STORAGE_PREFIX}:${encodeURIComponent(userId)}:`;
  const matches: Array<{
    scope: ProjectChatThreadScope;
    outcomeId: string;
  }> = [];
  try {
    for (let index = 0; index < target.length; index += 1) {
      const key = target.key(index);
      if (!key?.startsWith(keyPrefix)) continue;
      const raw = target.getItem(key);
      if (!raw || utf8ByteLength(raw) > MAX_PROJECT_CHAT_THREAD_BYTES) continue;
      let claimed: unknown;
      try {
        claimed = JSON.parse(raw);
      } catch {
        continue;
      }
      if (!claimed || typeof claimed !== "object" || Array.isArray(claimed)) {
        continue;
      }
      const projectId = (claimed as Record<string, unknown>).projectId;
      if (!nonEmptyString(projectId)) continue;
      const scope = { userId, projectId };
      if (projectChatThreadStorageKey(scope) !== key) continue;
      const thread = parseProjectChatThread(claimed, scope);
      const outcome = thread?.turns.find(
        (turn) =>
          turn.kind === "outcome" &&
          turn.status === "pending" &&
          turn.generation?.projectId === generation.projectId &&
          (!generation.requestId ||
            !turn.generation.requestId ||
            turn.generation.requestId === generation.requestId) &&
          (!generation.jobId ||
            !turn.generation.jobId ||
            turn.generation.jobId === generation.jobId),
      );
      if (outcome?.kind === "outcome") {
        matches.push({ scope, outcomeId: outcome.id });
      }
    }
  } catch {
    return null;
  }
  if (matches.length !== 1) return null;
  return settleProjectChatThreadOutcome(
    matches[0].scope,
    matches[0].outcomeId,
    settlement,
    target,
  );
}

export function retireProjectChatThreadRetry(
  scope: ProjectChatThreadScope,
  outcomeId: string,
  timestamp: number,
  storage?: Storage,
): ProjectChatThread | null {
  const existing = readProjectChatThread(scope, storage);
  const outcome = existing?.turns.find(
    (turn) => turn.kind === "outcome" && turn.id === outcomeId,
  );
  if (!outcome || outcome.kind !== "outcome" || !outcome.retry.retryable) {
    return null;
  }
  return settleProjectChatThreadOutcome(
    scope,
    outcomeId,
    {
      status: outcome.status === "pending" ? "failed" : outcome.status,
      text: outcome.text,
      retryable: false,
      timestamp,
    },
    storage,
  );
}

export function consumeProjectChatThreadTurn(
  scope: ProjectChatThreadScope,
  turnId: string,
  storage?: Storage,
): ProjectChatThread | null {
  const target = browserStorage(storage);
  if (!target || !nonEmptyString(turnId)) return null;
  const existing = readProjectChatThread(scope, target);
  if (!existing) return null;
  const turns = existing.turns.filter((turn) => turn.id !== turnId);
  if (turns.length === existing.turns.length) return null;
  const thread: ProjectChatThread = { ...existing, updatedAt: Date.now(), turns };
  try {
    target.setItem(projectChatThreadStorageKey(scope), JSON.stringify(thread));
    return thread;
  } catch {
    return null;
  }
}

export const PROJECT_CHAT_THREAD_RETRY_EVENT = "cogo:project-chat-thread-retry";

export interface ProjectChatThreadRetryRequest {
  scope: ProjectChatThreadScope;
  sourceOutcomeId: string;
  retry: ProjectChatRetryMetadata;
}

export function requestProjectChatThreadRetry(
  detail: ProjectChatThreadRetryRequest,
): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(PROJECT_CHAT_THREAD_RETRY_EVENT, { detail }));
}

export function latestProjectChatOutcome(
  thread: ProjectChatThread,
): Extract<ProjectChatThreadTurn, { kind: "outcome" }> | null {
  for (let index = thread.turns.length - 1; index >= 0; index -= 1) {
    const turn = thread.turns[index];
    if (turn?.kind === "outcome") return turn;
  }
  return null;
}
